import { LOCALE_NAMES } from "@repo/shared";
import type { LocaleCode, ProductDocumentLanguageLink } from "@repo/shared/types";
import { ExternalLink, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { usePagination } from "@/lib/hooks/use-pagination";
import { useProductDocumentsOverview } from "@/lib/hooks/use-portfolio-collections";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { TableCardSkeleton } from "../../skeleton-loaders";
import { useProductsTabContext } from "../products-tab-context";
import { T } from "./page.i18n";

export default function ProductsDocumentsPage() {
  const {
    collectionId,
    debouncedSearch,
    openEdit,
    openDelete,
    loadingProductId,
    checkingDeleteId,
  } = useProductsTabContext();
  const { overview, isLoading } = useProductDocumentsOverview(collectionId, debouncedSearch);
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const locale = userLanguage as LocaleCode;
  const localize = useLocalize();
  const {
    page,
    setPage,
    pageSize,
    totalCount,
    totalPages,
    pageItems: paginatedProducts,
  } = usePagination(overview.products, 10);

  if (isLoading && overview.products.length === 0 && !debouncedSearch) {
    return <TableCardSkeleton columns={3} />;
  }

  if (overview.documentTypes.length === 0) {
    return <EmptyState>{t.noDocumentTypes}</EmptyState>;
  }

  if (overview.products.length === 0 && debouncedSearch.trim() !== "") {
    return <EmptyState>{t.noProductsMatch}</EmptyState>;
  }

  if (overview.products.length === 0) {
    return <EmptyState>{t.noProducts}</EmptyState>;
  }

  return (
    <>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.productName}</TableHead>
            <TableHead>{t.documents}</TableHead>
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedProducts.map((product) => {
            // One line per type with at least one document; one chip per attached
            // language (the server already omits unsupported languages).
            const presentDocs = product.documents.filter((d) => d.documents.length > 0);
            return (
              <TableRow key={product.productId}>
                <TableCell className="font-medium">{product.productName}</TableCell>
                <TableCell>
                  {presentDocs.length === 0 ? (
                    <span className="text-muted-foreground italic text-xs">{t.notSet}</span>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {presentDocs.map((doc) => {
                        const typeLabel = localize(doc.documentTypeNameI18n);
                        return (
                          <div
                            key={doc.documentTypeId}
                            className="flex flex-wrap items-center gap-x-2 gap-y-1"
                          >
                            <span>{typeLabel}</span>
                            {doc.documents.map((link) => (
                              <DocumentLanguageChip
                                key={link.language}
                                link={link}
                                typeLabel={typeLabel}
                              />
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TableCell>
                <TableActionsCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.edit}
                    onClick={() => openEdit(product.productId)}
                    loading={loadingProductId === product.productId}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    tooltip={t.delete}
                    loading={checkingDeleteId === product.productId}
                    onClick={() =>
                      openDelete({
                        id: product.productId,
                        nameI18n: { [locale]: product.productName },
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableActionsCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </>
  );
}

// Small private chip: one attached language of a document type. A link when the
// document has an open URL; otherwise a muted, non-interactive chip so an attached
// document is still visible. Badge is intentionally not used — it is non-interactive
// by design (DESIGN.md › Badges) and these open documents.
function DocumentLanguageChip({
  link,
  typeLabel,
}: {
  link: ProductDocumentLanguageLink;
  typeLabel: string;
}) {
  const languageLabel = LOCALE_NAMES[link.language as LocaleCode] ?? link.language;

  if (!link.documentUrl) {
    return (
      <span className="inline-flex items-center rounded-sm border px-1.5 py-0.5 text-xs text-muted-foreground">
        {languageLabel}
      </span>
    );
  }

  return (
    <a
      href={link.documentUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-xs text-primary hover:bg-muted hover:underline"
      aria-label={`${typeLabel} – ${languageLabel}`}
    >
      {languageLabel}
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}
